import { Grid, Typography } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { Dispatch, FC } from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { collectItemAndDesignFeePairs } from '../../../lib/cart/item'
import { CartStateReduceAction } from '../../../lib/cart/reducers/cart-state'
import { useDispensaryCartStatus } from '../../../lib/dispensaries/events/cart'
import CartItemContainer from '../../cart/CartItemContainer'
import { CartUpsellFreeShipping } from '../../global/cart/CartUpsellFreeShipping'
import Spinner from '../../Spinner'
import CartItem from './CartItem'

export interface CartItemListProps {
  cart: CartFragment
  dispatch: Dispatch<CartStateReduceAction>
}

const StyledEmptyCartText = styled(Typography)`
  padding: 15px;
  font-weight: bold;
`

const CartItems: FC<Pick<CartItemListProps, 'cart' | 'dispatch'>> = React.memo(
  ({ cart, dispatch }) => {
    const { featureFlags } = useSiteMetadata() ?? {}
    const { id: cartId, items, shipping_addresses: shippingAddresses } = cart
    const [shippingAddress] = shippingAddresses || []

    // if label status (PL) feature flag enabled, pair items with design fee items efficiently
    if (featureFlags?.productLabelStatus) {
      const elements: React.ReactElement[] = []
      for (const pair of collectItemAndDesignFeePairs(items ?? []).values()) {
        elements.push(
          <CartItemContainer
            cartId={cartId}
            component={CartItem}
            item={pair.item}
            labelDesignFeeItem={pair.designFeeItem}
            key={pair.item.id}
            requestDispatch={dispatch}
            shippingAddress={shippingAddress}
          />,
        )
      }
      return <>{elements}</>
    }

    const elements: React.ReactElement[] = (items ?? []).reduce(
      (acc: React.ReactElement[], item) => {
        if (!item) return acc
        acc.push(
          <CartItemContainer
            cartId={cartId}
            component={CartItem}
            item={item}
            key={item.id}
            requestDispatch={dispatch}
            shippingAddress={shippingAddress}
          />,
        )
        return acc
      },
      [],
    )

    return <>{elements}</>
  },
)

CartItems.displayName = 'CartItems'

export const CartItemList: FC<CartItemListProps> = ({ cart, dispatch }) => {
  const { loading } = useDispensaryCartStatus()
  const { items } = cart

  if (loading) {
    return (
      <Grid container item xs={12}>
        <Spinner loading />
      </Grid>
    )
  }

  return (
    <Grid container item xs={12}>
      <Subscribe fallback={<Spinner loading />}>
        {items?.length ? (
          <CartUpsellFreeShipping
            cartPriceTotal={cart?.prices?.subtotal_with_discount_excluding_tax?.value}
            shippingAddress={cart?.shipping_addresses[0]}
          />
        ) : null}
        {items?.length ? (
          <CartItems cart={cart} dispatch={dispatch} />
        ) : (
          <Grid alignItems="center" justifyContent="center" container item xs={12}>
            <StyledEmptyCartText variant="body1">Your cart is empty</StyledEmptyCartText>
          </Grid>
        )}
      </Subscribe>
    </Grid>
  )
}
